import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useThemeColor } from '@/hooks/use-theme-color';

interface ChatEmptyStateProps {
  accentColor: string;
  textColor: string;
  onPromptPress?: (prompt: string) => void;
}

const LIGHT_IMAGE = require('@/assets/images/aichat.png');
const DARK_IMAGE = require('@/assets/images/aichat-dark.png');

const STARTER_PROMPTS = [
  "What's on today?",
  'Add milk to my shopping list',
  'When is everyone free this weekend?',
];

export function ChatEmptyState({ accentColor, textColor, onPromptPress }: ChatEmptyStateProps) {
  const colorScheme = useColorScheme();
  const imageSource = colorScheme === 'dark' ? DARK_IMAGE : LIGHT_IMAGE;
  const cardBackground = useThemeColor({}, 'cardBackground');

  return (
    <View style={styles.container}>
      <Image source={imageSource} style={styles.image} />
      <View style={[styles.textCard, { backgroundColor: cardBackground }]}>
        <Text style={[styles.title, { color: accentColor }]}>Chat with Fim</Text>
        <Text style={[styles.subtitle, { color: textColor, opacity: 0.7 }]}>
          Ask about your schedule, tasks, or family plans
        </Text>
      </View>

      {onPromptPress && (
        <View style={styles.promptList}>
          {STARTER_PROMPTS.map((prompt) => (
            <Pressable
              key={prompt}
              onPress={() => onPromptPress(prompt)}
              style={({ pressed }) => [
                styles.promptChip,
                {
                  backgroundColor: cardBackground,
                  borderColor: accentColor,
                  opacity: pressed ? 0.6 : 1,
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Send prompt: ${prompt}`}
            >
              <Text style={[styles.promptText, { color: textColor }]}>{prompt}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xxl,
    gap: Spacing.md,
  },
  image: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  textCard: {
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.lg,
    borderRadius: BorderRadius.lg,
    gap: Spacing.xs,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 13,
    lineHeight: 18,
  },
  promptList: {
    alignItems: 'center',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  promptChip: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.xl,
    borderWidth: 1,
  },
  promptText: {
    fontSize: 14,
  },
});
